import { useRef } from 'react'

const SWIPE_MIN_PX = 60

/**
 * Touch handlers that fire `onSwipeLeft` / `onSwipeRight` for a deliberate
 * sideways swipe. A gesture starting in a field or a sideways-scrolling row
 * belongs to that element, so it is ignored.
 */
export function useHorizontalSwipe({
  enabled,
  onSwipeLeft,
  onSwipeRight,
}: {
  enabled: boolean
  onSwipeLeft: () => void
  onSwipeRight: () => void
}) {
  const start = useRef<{ x: number; y: number } | undefined>(undefined)

  const onTouchStart = (event: React.TouchEvent) => {
    start.current = undefined
    if (!enabled || event.touches.length !== 1) return
    const target = event.target as HTMLElement
    if (target.closest(`input, textarea, select, [contenteditable="true"]`)) return
    for (let el: HTMLElement | null = target; el && el !== event.currentTarget; el = el.parentElement) {
      const overflowX = getComputedStyle(el).overflowX
      if (el.scrollWidth > el.clientWidth && (overflowX === `auto` || overflowX === `scroll`)) return
    }
    start.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }
  }

  const onTouchEnd = (event: React.TouchEvent) => {
    const origin = start.current
    start.current = undefined
    if (!origin) return
    const dx = event.changedTouches[0].clientX - origin.x
    const dy = event.changedTouches[0].clientY - origin.y
    // Mostly sideways, so a vertical scroll that drifts never counts.
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 2) return
    if (dx < 0) onSwipeLeft()
    else onSwipeRight()
  }

  return { onTouchStart, onTouchEnd }
}
